import {
  useMutation,
  useQueryClient,
  UseMutationResult,
  UseMutationOptions,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import type {
  EventTicketTierDto,
  UpdateEventTicketTierDto,
  ResponseDto,
} from "@mimber/schema"
import { eventTicketTiersKeys } from "../events.utils"

export type UpdateEventTicketTierVariables = {
  tierUuid: string
  data: UpdateEventTicketTierDto
}

export const useUpdateEventTicketTier = (
  eventUuid: string,
  options?: UseMutationOptions<
    ResponseDto<EventTicketTierDto>,
    Error,
    UpdateEventTicketTierVariables
  >
): UseMutationResult<
  ResponseDto<EventTicketTierDto>,
  Error,
  UpdateEventTicketTierVariables
> => {
  guardAppUsage("mimber-organization", "useUpdateEventTicketTier")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async ({ tierUuid, data }: UpdateEventTicketTierVariables) => {
      const response = await server.patch<ResponseDto<EventTicketTierDto>>(
        `events/${eventUuid}/ticket-tiers/${tierUuid}`,
        data
      )
      return response.data
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: eventTicketTiersKeys.findAll(eventUuid),
      })
      options?.onSuccess?.(data, variables, onMutateResult, context)
    },
  })
}
